/**
 * Tenant_admin Activity page.
 *
 * Two lenses over the same baseline+session stream, chosen with a toggle:
 *   - "Activity" — a chronological feed (Today / Yesterday / …) that answers
 *     "what happened, and who's been practising?" at a glance.
 *   - "By participant" — collapsible per-participant cards, each with a
 *     date-derived status + summary in the header and the full capture
 *     timeline on expand.
 *
 * A filter bar (search · procedure · when · status · department) narrows both
 * lenses. Every per-event action from the prior design is preserved — Download
 * (session ZIP bundle: NDJSON + video + audio / baseline JSON fallback), Attach, Replay, Split, Tag,
 * Disable/Restore — surfaced through the shared CaptureTimeline action menu in
 * the participant view and the same menu items (buildMenuItems) from a kebab in
 * the feed. Read-only admins keep the view-only treatment.
 *
 * Deliberately NOT shown: readiness %, consistency, per-participant AI coach,
 * expert/needs-review — there's no backing metric yet, so the page only
 * surfaces what the timestamps + baseline presence honestly support.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { RecordingFilters } from '../../components/RecordingFilters';
import {
  EMPTY_FILTER,
  filterOptions,
  isFilterActive,
  matchesFilter,
} from '../../components/recordingFilterRules';
import { useFilterParams } from '../../components/useFilterParams';
import {
  mergeOptions,
  toSessionQuery,
  useDebounced,
  useSessionFilterOptions,
} from '../../components/serverFilter';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewListIcon from '@mui/icons-material/ViewList';
import GroupsIcon from '@mui/icons-material/Groups';
import DevicesIcon from '@mui/icons-material/Devices';

import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import * as baselinesApi from '../../api/baselines';
import * as sessionsApi from '../../api/sessions';
import { useFormatDateTime, useFormatTime } from '../../util/displayTimeZone';
import * as orgApi from '../../api/organizations';
import { AttachmentsDialog } from '../../components/AttachmentsDialog';
import { SessionReplayDialog } from '../../components/SessionReplayDialog';
import { SplitSessionDialog } from '../../components/SplitSessionDialog';
import { extractErrorMessage } from '../../api/errors';
import {
  CaptureTimeline,
  buildMenuItems,
  buildTimelineEvents,
  groupByDevice,
  type TimelineEvent,
} from '../../components/CaptureTimeline';
import { RefreshButton } from '../../components/RefreshButton';
import { SessionTagDialog } from './SessionTagDialog';
import {
  SearchableSelect,
  type SelectOption,
} from '../../components/SearchableSelect';
import { procedureColor, BASELINE_COLOR } from '../../util/procedureColors';
import {
  summarizeParticipant,
  relativeDay,
  groupIntoFeed,
  type ParticipantStatus,
  type ParticipantSummary,
} from '../../util/activityInsights';

function groupByParticipant(events: TimelineEvent[]): {
  key: string;
  label: string;
  email: string | null;
  events: TimelineEvent[];
}[] {
  // Bucket events by participant_user_id. Rows with a null id are
  // grouped under "Unattributed" — those predate the participant-id
  // column or come from a calibration capture with no picker pick.
  const buckets = new Map<
    string,
    { label: string; email: string | null; events: TimelineEvent[] }
  >();
  for (const event of events) {
    const key = event.participantUserId ?? 'unattributed';
    const existing = buckets.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      const label =
        event.participantLabel ??
        (event.participantUserId === null ? 'Unattributed' : event.participantUserId);
      buckets.set(key, {
        label,
        email: emailFromLabel(event.participantLabel),
        events: [event],
      });
    }
  }
  return [...buckets.entries()]
    .map(([key, value]) => ({ key, label: value.label, email: value.email, events: value.events }))
    .sort((a, b) => {
      // A→Z by participant name so the list keeps a stable order when a
      // session is disabled/restored (a most-recent-activity sort
      // reshuffled the cards on every disable). Unattributed sinks last.
      if (a.key === 'unattributed') return 1;
      if (b.key === 'unattributed') return -1;
      return a.label.localeCompare(b.label, undefined, {
        sensitivity: 'base',
      });
    });
}

// A label like "Pat Patient" stays as the name; a label that looks
// like an email is split into a separate field for the card subtitle.
// Defensive against participant_label being whatever the capture-app
// happened to send.
function emailFromLabel(label: string | null): string | null {
  if (!label) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(label.trim()) ? label.trim() : null;
}

function downloadBaselineJsonRow(baseline: baselinesApi.Baseline): void {
  // Same code-path the old TenantBaselines page used for the
  // file-icon button. The row's metrics_json summary lives in here
  // alongside the participant + device labels; analysts can paste
  // straight into a notebook.
  const safeParticipant =
    (baseline.participant_label ?? 'baseline').replace(
      /[^A-Za-z0-9._-]+/g,
      '_',
    );
  const captured = baseline.captured_at.replace(/[:.]/g, '-');
  const filename = `${safeParticipant}-baseline-${captured}.json`;
  const blob = new Blob([JSON.stringify(baseline, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

type StatusFilter = 'all' | ParticipantStatus | 'missing_baseline';

const STATUS_OPTIONS: SelectOption<StatusFilter>[] = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: '🟢 Active' },
  { value: 'idle', label: '🟡 Idle' },
  { value: 'dormant', label: '⚪ Dormant' },
  { value: 'missing_baseline', label: 'Missing baseline' },
];
const ALL_DEPARTMENTS = [{ value: '', label: 'All departments' }];

/** Feed date-range → the set of feed buckets that count as "within" it. */

const STATUS_COLOR: Record<ParticipantStatus, string> = {
  active: 'success.main',
  idle: 'warning.main',
  dormant: 'text.disabled',
};

/** Small recency dot beside a participant name. */
function StatusDot({ status }: { status: ParticipantStatus }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: STATUS_COLOR[status],
      }}
    />
  );
}

/** A row of colour-coded dots — one per distinct procedure practised. Lets an
 *  operator recognise a participant's mix at a glance without reading labels. */
function ProcedureDots({
  procedures,
}: {
  procedures: ParticipantSummary['procedures'];
}) {
  if (procedures.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {procedures.slice(0, 6).map((p) => (
        <Tooltip key={p.name} title={`${p.name} · ${p.count}`}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: procedureColor(p.name),
            }}
          />
        </Tooltip>
      ))}
      {procedures.length > 6 && (
        <Typography variant="caption" color="text.secondary">
          +{procedures.length - 6}
        </Typography>
      )}
    </Stack>
  );
}

/** Department · location context line for a feed row (names pre-resolved). */
function orgLine(event: TimelineEvent): string {
  return [event.departmentName, event.locationName].filter(Boolean).join(' · ');
}

export function TenantActivity() {
  const { user } = useAuth();
  const fmtTime = useFormatTime();
  const fmtDateTime = useFormatDateTime();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);
  const [sessions, setSessions] = useState<sessionsApi.CaptureSession[] | null>(
    null,
  );
  const [baselines, setBaselines] = useState<baselinesApi.Baseline[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Paging (PORTAL-SESSIONS-PAGING): the list used to stop silently at the
  // 200 most recent; now it says how many there are and loads the rest.
  const [sessionsTotal, setSessionsTotal] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [attachEvent, setAttachEvent] = useState<TimelineEvent | null>(null);
  // Replay is a view (read), so it's offered to every admin including
  // read-only — not gated by canWrite.
  const [replayEvent, setReplayEvent] = useState<TimelineEvent | null>(null);
  const [splitEvent, setSplitEvent] = useState<TimelineEvent | null>(null);
  // Live monitoring moved to its own "Live Recording" page (LIVE-OR-STREAMING);
  // the Activity page is now purely historical.
  // Disabled captures are hidden by default to keep the timeline clean;
  // the "Show disabled" toggle brings them back (dimmed, with a Restore
  // button) when an operator needs to re-enable or audit them.

  const [showDisabled, setShowDisabled] = useState(false);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Disable confirmation — null when the styled dialog is closed.
  const [disableTarget, setDisableTarget] = useState<TimelineEvent | null>(
    null,
  );
  // Departments + locations for the tagging dialog (RFC procedure-instance,
  // Phase 1b). Fetched alongside the activity so the selects are populated.
  const [departments, setDepartments] = useState<orgApi.Department[]>([]);
  const [locations, setLocations] = useState<orgApi.Location[]>([]);
  // Tagging dialog — null when closed; the form fields prefill from the
  // session's current context on open.
  // Which event's tag dialog is open (null = closed). The form state +
  // save live in <SessionTagDialog>.
  const [tagEvent, setTagEvent] = useState<TimelineEvent | null>(null);

  // View + filter state (Activity redesign). All client-side over the
  // already-loaded event stream — no extra fetches. Defaults to the
  // per-participant lens (the roster operators reason about); the feed is the
  // "what happened today" alternative a click away.
  const [view, setView] = useState<'feed' | 'participants' | 'devices'>(
    'participants',
  );
  // Participant / device / procedure / date live in the shared filter so this
  // screen asks the same questions, the same way, as the dashboards and the
  // case builder. Status and Department stay local — they are Activity's own
  // lenses and mean nothing on the other screens.
  // In the URL, so Back undoes a filter instead of leaving the page.
  const [filter, setFilter] = useFilterParams('act');
  // Held still for a moment: the dropdowns fire once per click, but the
  // exact-range inputs fire once per edit, and each change is a request.
  const serverFilter = useDebounced(filter);
  const filterSource = useSessionFilterOptions(tenantId, showDisabled);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [department, setDepartment] = useState('');
  // Which participant cards are expanded (participant view). Collapsed by
  // default so a long roster is scannable; click a header to open.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Feed kebab menu — anchored to whichever row's ⋮ was clicked.
  const [feedMenu, setFeedMenu] = useState<{
    anchor: HTMLElement;
    event: TimelineEvent;
  } | null>(null);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    setRefreshing(true);
    try {
      const [s, b, d, l] = await Promise.all([
        // Filtered by the SERVER, before paging — so the pages are pages of
        // the matches, and the total counts all of them.
        sessionsApi.listCaptureSessionsPage(
          tenantId,
          showDisabled,
          toSessionQuery(serverFilter, filterSource.participantIds),
        ),
        baselinesApi.listBaselinesForTenant(tenantId, showDisabled),
        orgApi.listDepartments(tenantId),
        orgApi.listLocations(tenantId),
      ]);
      setSessions(s.sessions);
      setSessionsTotal(s.total);
      setNextCursor(s.nextCursor);
      setBaselines(b);
      setDepartments(d);
      setLocations(l);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load activity'));
      setSessions([]);
      setSessionsTotal(null);
      setNextCursor(null);
      setBaselines([]);
    } finally {
      setRefreshing(false);
    }
    // Re-fetches when the filter changes: the narrowing happens in SQL now,
    // so a new filter is a new query rather than a new pass over cached rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, showDisabled, JSON.stringify(serverFilter), filterSource.participantIds]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const loadMore = useCallback(async () => {
    if (!tenantId || !nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await sessionsApi.listCaptureSessionsPage(
        tenantId,
        showDisabled,
        toSessionQuery(serverFilter, filterSource.participantIds),
        nextCursor,
      );
      setSessions((prev) => [...(prev ?? []), ...page.sessions]);
      setSessionsTotal(page.total);
      setNextCursor(page.nextCursor);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load more recordings'));
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, nextCursor, showDisabled, JSON.stringify(serverFilter), filterSource.participantIds]);

  const events = useMemo(() => {
    const deptById = new Map(departments.map((d) => [d.id, d.name]));
    const locById = new Map(locations.map((l) => [l.id, l.name]));
    // Resolve the department/location IDs to names so the timeline can
    // show the tag (the tile only carries IDs).
    return buildTimelineEvents(sessions ?? [], baselines ?? []).map((e) => ({
      ...e,
      departmentName: e.departmentId ? deptById.get(e.departmentId) : null,
      locationName: e.locationId ? locById.get(e.locationId) : null,
    }));
  }, [sessions, baselines, departments, locations]);

  // Full (unfiltered) participant buckets + their honest summaries. The
  // summary is computed from ALL of a participant's events so baseline
  // presence + status aren't distorted by an active filter; the filtered
  // stream below only decides which events (and thus which participants)
  // are shown.
  const fullBuckets = useMemo(() => groupByParticipant(events), [events]);
  const summaryByKey = useMemo(() => {
    const m = new Map<string, ParticipantSummary>();
    for (const b of fullBuckets) m.set(b.key, summarizeParticipant(b.events));
    return m;
  }, [fullBuckets]);

  // Only values actually present, so a pick can never return an empty screen.
  const filterableEvents = useMemo(
    () =>
      events.map((e) => ({
        participantLabel: e.participantLabel,
        deviceLabel: e.deviceLabel,
        procedureName: e.procedureName,
        startedAt: e.capturedAt,
      })),
    [events],
  );
  // Server options (the whole session history) unioned with the ones present
  // in the rows — the latter covers baselines, which the server's session
  // query knows nothing about.
  const options = useMemo(
    () => mergeOptions(filterSource.options, filterOptions(filterableEvents)),
    [filterSource.options, filterableEvents],
  );
  const departmentOptions = useMemo(() => {
    const s = new Set<string>();
    for (const e of events) if (e.departmentName) s.add(e.departmentName);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const statusKeys =
      status === 'all'
        ? null
        : new Set(
            fullBuckets
              .filter((b) => {
                const sum = summaryByKey.get(b.key);
                if (!sum) return false;
                return status === 'missing_baseline'
                  ? !sum.hasBaseline
                  : sum.status === status;
              })
              .map((b) => b.key),
          );
    return events.filter((e) => {
      if (department && e.departmentName !== department) return false;
      if (statusKeys) {
        const key = e.participantUserId ?? 'unattributed';
        if (!statusKeys.has(key)) return false;
      }
      return matchesFilter(
        {
          participantLabel: e.participantLabel,
          deviceLabel: e.deviceLabel,
          procedureName: e.procedureName,
          startedAt: e.capturedAt,
          processingFailed: e.processingFailed,
        },
        filter,
      );
    });
  }, [
    events,
    filter,
    department,
    status,
    fullBuckets,
    summaryByKey,
  ]);

  const feedGroups = useMemo(() => groupIntoFeed(filteredEvents), [
    filteredEvents,
  ]);
  const participantGroups = useMemo(
    () => groupByParticipant(filteredEvents),
    [filteredEvents],
  );
  const deviceGroups = useMemo(
    () => groupByDevice(filteredEvents),
    [filteredEvents],
  );

  const anyFilterActive =
    isFilterActive(filter) || department !== '' || status !== 'all';

  const clearFilters = () => {
    setFilter({ ...EMPTY_FILTER });
    setDepartment('');
    setStatus('all');
  };

  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Expand-all operates on whichever grouped view is active (by-participant
  // or by-device); the feed view has no groups to expand.
  const groupedKeys =
    view === 'devices'
      ? deviceGroups.map((g) => g.key)
      : participantGroups.map((b) => b.key);
  const allExpanded =
    groupedKeys.length > 0 && groupedKeys.every((k) => expanded.has(k));
  const toggleExpandAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(groupedKeys));

  async function handleDownload(event: TimelineEvent) {
    if (!tenantId) return;
    setDownloadingId(event.id);
    setDownloadError(null);
    try {
      if (event.kind === 'session') {
        const { url, filename } =
          await sessionsApi.getCaptureSessionDownload(tenantId, event.id);
        anchorClickDownload(url, filename);
      } else if (event.s3Key !== null) {
        const { url, filename } = await baselinesApi.getBaselineDownload(
          tenantId,
          event.id,
        );
        anchorClickDownload(url, filename);
      } else {
        downloadBaselineJsonRow(event.raw as baselinesApi.Baseline);
      }
    } catch (err: unknown) {
      setDownloadError(extractErrorMessage(err, 'download failed'));
    } finally {
      setDownloadingId(null);
    }
  }

  // Open the styled confirm dialog (replaces window.confirm).
  function handleDisable(event: TimelineEvent) {
    setActionError(null);
    setDisableTarget(event);
  }

  async function confirmDisable() {
    if (!tenantId || !disableTarget) return;
    const event = disableTarget;
    setMutatingId(event.id);
    setActionError(null);
    try {
      if (event.kind === 'session') {
        await sessionsApi.disableCaptureSession(tenantId, event.id);
      } else {
        await baselinesApi.disableBaseline(tenantId, event.id);
      }
      setDisableTarget(null);
      await reload();
    } catch (err: unknown) {
      setActionError(extractErrorMessage(err, 'disable failed'));
      setDisableTarget(null);
    } finally {
      setMutatingId(null);
    }
  }

  async function handleRestore(event: TimelineEvent) {
    if (!tenantId) return;
    setMutatingId(event.id);
    setActionError(null);
    try {
      if (event.kind === 'session') {
        await sessionsApi.restoreCaptureSession(tenantId, event.id);
      } else {
        await baselinesApi.restoreBaseline(tenantId, event.id);
      }
      await reload();
    } catch (err: unknown) {
      setActionError(extractErrorMessage(err, 'restore failed'));
    } finally {
      setMutatingId(null);
    }
  }

  // Handler bundle passed to both the participant timeline and the feed
  // kebab — canWrite gating identical to the prior design.
  const timelineHandlers = {
    onDownload: canWrite ? handleDownload : undefined,
    onAttach: canWrite ? (e: TimelineEvent) => setAttachEvent(e) : undefined,
    onTag: canWrite ? (e: TimelineEvent) => setTagEvent(e) : undefined,
    onDisable: canWrite ? handleDisable : undefined,
    onRestore: canWrite ? handleRestore : undefined,
    onReplay: (e: TimelineEvent) => setReplayEvent(e),
    onSplit: canWrite ? (e: TimelineEvent) => setSplitEvent(e) : undefined,
  };

  if (!tenantId) {
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role.
      </Alert>
    );
  }
  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }
  if (sessions === null || baselines === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4">Activity</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
              />
            }
            label="Show disabled"
          />
          <RefreshButton
            onClick={() => void reload()}
            refreshing={refreshing}
          />
        </Stack>
      </Stack>


      {/* View toggle + filter bar. All client-side over the loaded stream. */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          // flex-start, not center: the filter bar wraps its date chips onto a
          // second line here (five controls leave no room), and centring made
          // the view toggle float between the two rows instead of sitting on
          // the top line with the dropdowns.
          alignItems={{ md: 'flex-start' }}
          sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
            aria-label="Activity view"
          >
            <ToggleButton value="feed" aria-label="Activity feed">
              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
              Activity
            </ToggleButton>
            <ToggleButton value="participants" aria-label="By participant">
              <GroupsIcon fontSize="small" sx={{ mr: 0.5 }} />
              By participant
            </ToggleButton>
            <ToggleButton value="devices" aria-label="By device">
              <DevicesIcon fontSize="small" sx={{ mr: 0.5 }} />
              By device
            </ToggleButton>
          </ToggleButtonGroup>

          <RecordingFilters
            value={filter}
            onChange={setFilter}
            options={options}
            // The one screen where "which recordings never scored?" is a
            // question someone actually asks — this is where an operator goes
            // to investigate.
            show={{ processing: true }}
            // `events` is now itself a filtered fetch, so "12 of 12" would
            // be a tautology dressed up as a ratio.
            summary={
              isFilterActive(filter)
                ? `${filteredEvents.length} matching`
                : `${filteredEvents.length} recordings`
            }
          >
            {/* Activity statuses run most → least recent, so keep that order. */}
            <SearchableSelect<StatusFilter>
              keepOrder
              label="Status"
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
              sx={{ minWidth: 150 }}
            />
            {departmentOptions.length > 0 && (
              <SearchableSelect
                label="Department"
                value={department}
                onChange={setDepartment}
                pinned={ALL_DEPARTMENTS}
                options={departmentOptions.map((d) => ({ value: d, label: d }))}
                sx={{ minWidth: 150 }}
              />
            )}
          </RecordingFilters>
          {anyFilterActive && (
            <Button size="small" onClick={clearFilters}>
              Clear
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {(view === 'participants' || view === 'devices') &&
            groupedKeys.length > 0 && (
              <Button size="small" onClick={toggleExpandAll}>
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
        </Stack>
      </Paper>

      {downloadError && (
        <Alert severity="error" onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {!canWrite && (
        <Alert severity="info">
          Your admin account is <strong>read-only</strong>. You can view
          the activity timeline but can't download recordings, attach
          files, or disable and restore captures.
        </Alert>
      )}

      {events.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activity yet. The capture-app posts here as soon as an
            operator records the first baseline or session for any
            participant in your tenant.
          </Typography>
        </Paper>
      ) : filteredEvents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activity matches these filters.{' '}
            {anyFilterActive && (
              <Button size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Typography>
        </Paper>
      ) : view === 'feed' ? (
        /* ── Activity feed ─────────────────────────────────────────── */
        <Stack spacing={2}>
          {feedGroups.map((group) => (
            <Box key={group.key}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  color: 'text.secondary',
                }}
              >
                {group.label}
              </Typography>
              <Card variant="outlined" sx={{ mt: 0.5 }}>
                {group.events.map((event, i) => {
                  const isSession = event.kind === 'session';
                  const isDeleted = event.deletedAt !== null;
                  const dotColor = isSession
                    ? procedureColor(event.procedureName)
                    : BASELINE_COLOR;
                  const title = isSession
                    ? event.label || event.procedureName || 'Session'
                    : 'Baseline';
                  const org = orgLine(event);
                  const canAct = isSession || canWrite;
                  return (
                    <Box
                      key={event.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderTop: i === 0 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        opacity: isDeleted ? 0.55 : 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          width: 52,
                          flexShrink: 0,
                          fontWeight: 700,
                          color: 'text.secondary',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {fmtTime(event.capturedAt)}
                      </Typography>
                      <Box
                        sx={{
                          width: 11,
                          height: 11,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: dotColor,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          noWrap
                          sx={{ fontWeight: 750, fontSize: '0.9rem' }}
                        >
                          {event.participantLabel ?? 'Unattributed'}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ mt: 0.25, flexWrap: 'wrap' }}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={title}
                            sx={{
                              height: 20,
                              fontWeight: 700,
                              color: dotColor,
                              borderColor: dotColor,
                              '& .MuiChip-label': {
                                px: 0.75,
                                fontSize: '0.68rem',
                              },
                            }}
                          />
                          {org && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {org}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      {isDeleted && (
                        <Chip
                          size="small"
                          color="error"
                          label="Disabled"
                          sx={{
                            height: 20,
                            fontSize: '0.62rem',
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {canAct && (
                        <IconButton
                          size="small"
                          aria-label="Actions"
                          onClick={(e) =>
                            setFeedMenu({ anchor: e.currentTarget, event })
                          }
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Card>
            </Box>
          ))}
        </Stack>
      ) : view === 'devices' ? (
        /* ── By device (collapsible) ───────────────────────────────── */
        <Stack spacing={1.5}>
          {deviceGroups.map((bucket) => {
            const isOpen = expanded.has(bucket.key);
            const sessionCount = bucket.events.filter(
              (e) => e.kind === 'session',
            ).length;
            const baselineCount = bucket.events.length - sessionCount;
            // Events are newest-first, so [0] is the most recent capture.
            const lastActivity = bucket.events[0]?.capturedAt ?? null;
            return (
              <Card key={bucket.key} variant="outlined">
                <CardContent sx={{ '&:last-child': { pb: isOpen ? 2 : 1.5 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    onClick={() => toggleExpand(bucket.key)}
                    role="button"
                    aria-expanded={isOpen}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        color: 'text.secondary',
                        transform: isOpen ? 'none' : 'rotate(-90deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                    <DevicesIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography noWrap sx={{ fontWeight: 750, minWidth: 0 }}>
                      {bucket.label}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={`${sessionCount} session${sessionCount === 1 ? '' : 's'}`}
                        sx={{ height: 22, fontWeight: 700 }}
                      />
                      {baselineCount > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${baselineCount} baseline${baselineCount === 1 ? '' : 's'}`}
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      )}
                      {lastActivity && (
                        <Typography variant="caption" color="text.secondary">
                          {relativeDay(lastActivity)}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ mt: 1 }}>
                      <CaptureTimeline
                        events={bucket.events}
                        busyId={downloadingId ?? mutatingId}
                        onDownload={timelineHandlers.onDownload}
                        onAttach={timelineHandlers.onAttach}
                        onDisable={timelineHandlers.onDisable}
                        onRestore={timelineHandlers.onRestore}
                        onReplay={timelineHandlers.onReplay}
                        onSplit={timelineHandlers.onSplit}
                        onTag={timelineHandlers.onTag}
                        actionMenu
                        // Grouped by device → name who recorded each session.
                        secondaryLabel="participant"
                      />
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* ── By participant (collapsible) ──────────────────────────── */
        <Stack spacing={1.5}>
          {participantGroups.map((bucket) => {
            const summary =
              summaryByKey.get(bucket.key) ??
              summarizeParticipant(bucket.events);
            const isOpen = expanded.has(bucket.key);
            return (
              <Card key={bucket.key} variant="outlined">
                <CardContent
                  sx={{ '&:last-child': { pb: isOpen ? 2 : 1.5 } }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    onClick={() => toggleExpand(bucket.key)}
                    role="button"
                    aria-expanded={isOpen}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        color: 'text.secondary',
                        transform: isOpen ? 'none' : 'rotate(-90deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                    <StatusDot status={summary.status} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 750 }}>
                        {bucket.label}
                      </Typography>
                      {bucket.email && bucket.email !== bucket.label && (
                        <Typography
                          noWrap
                          variant="caption"
                          color="text.secondary"
                        >
                          {bucket.email}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={`${summary.sessionCount} session${
                          summary.sessionCount === 1 ? '' : 's'
                        }`}
                        sx={{ height: 22, fontWeight: 700 }}
                      />
                      {summary.hasBaseline ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label="✓ baseline"
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label="Needs baseline"
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {relativeDay(summary.lastActivity)}
                      </Typography>
                      <ProcedureDots procedures={summary.procedures} />
                    </Stack>
                  </Stack>
                  <Collapse in={isOpen} unmountOnExit>
                    {/* Full capture timeline — unchanged behaviour, every
                        action preserved via the shared action menu. */}
                    <Box sx={{ mt: 1 }}>
                      <CaptureTimeline
                        events={bucket.events}
                        busyId={downloadingId ?? mutatingId}
                        onDownload={timelineHandlers.onDownload}
                        onAttach={timelineHandlers.onAttach}
                        onDisable={timelineHandlers.onDisable}
                        onRestore={timelineHandlers.onRestore}
                        onReplay={timelineHandlers.onReplay}
                        onSplit={timelineHandlers.onSplit}
                        onTag={timelineHandlers.onTag}
                        actionMenu
                        // Grouped by participant → name the device each
                        // session was recorded on.
                        secondaryLabel="device"
                      />
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Shared feed kebab menu — same action set as the participant
          timeline, built from the exported buildMenuItems. */}
      <Menu
        anchorEl={feedMenu?.anchor ?? null}
        open={Boolean(feedMenu)}
        onClose={() => setFeedMenu(null)}
      >
        {feedMenu
          ? buildMenuItems(feedMenu.event, {
              ...timelineHandlers,
              close: () => setFeedMenu(null),
            })
          : null}
      </Menu>

      {attachEvent && (
        <AttachmentsDialog
          open
          onClose={() => setAttachEvent(null)}
          sessionRowId={attachEvent.id}
          sessionLabel={`${
            attachEvent.procedureName ?? 'Session'
          } · ${fmtDateTime(attachEvent.capturedAt)}`}
          tenantId={tenantId}
        />
      )}
      {replayEvent && (
        <SessionReplayDialog
          open
          onClose={() => setReplayEvent(null)}
          title={`${replayEvent.procedureName ?? 'Session'}${
            replayEvent.participantLabel
              ? ` · ${replayEvent.participantLabel}`
              : ''
          } · ${fmtDateTime(replayEvent.capturedAt)}`}
          fetchUrl={() =>
            sessionsApi.getSessionReplayUrl(tenantId, replayEvent.id)
          }
        />
      )}
      {splitEvent && (
        <SplitSessionDialog
          open
          onClose={() => setSplitEvent(null)}
          tenantId={tenantId}
          event={splitEvent}
          onSplitDone={() => void reload()}
        />
      )}

      {/* Tag a recording with its department / location / kind / case ref
          (RFC procedure-instance, Phase 1b). */}
      <SessionTagDialog
        event={tagEvent}
        tenantId={tenantId}
        departments={departments}
        locations={locations}
        onClose={() => setTagEvent(null)}
        onSaved={reload}
      />

      <Dialog
        open={disableTarget !== null}
        onClose={() => (mutatingId ? null : setDisableTarget(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Disable this{' '}
          {disableTarget?.kind === 'session' ? 'recording' : 'baseline'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            It will be excluded from scores and faculty comparisons, but
            nothing is deleted. It stays on the timeline, dimmed, and you
            can restore it at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisableTarget(null)}
            disabled={mutatingId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDisable()}
            color="error"
            variant="contained"
            disabled={mutatingId !== null}
          >
            {mutatingId !== null ? 'Disabling…' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
      {sessions && sessionsTotal !== null && sessionsTotal > sessions.length && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing the {sessions.length} most recent of {sessionsTotal} recordings
          </Typography>
          {nextCursor && (
            <Button variant="outlined" size="small" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function anchorClickDownload(url: string, filename: string): void {
  // Same anchor-click pattern the prior Sessions / Baselines pages
  // used — avoids the new-tab black flash that window.open produces
  // against download URLs.
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
